import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { HeartHandshake, ShoppingBag, Ticket, IdCard, Loader2 } from 'lucide-react';
import { PaymentError } from '../components/PaymentError';

const CAT_ICON = { Membership: IdCard, Donation: HeartHandshake, Events: Ticket, Merchandise: ShoppingBag };
const CAT_ORDER = ['Membership', 'Donation', 'Events', 'Merchandise'];
const CTA = { Membership: 'Renew', Donation: 'Donate', Events: 'Buy', Merchandise: 'Buy' };
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Support Us lists the same items as the Shop (managed in Admin → Shop) and checks out through the Shop.
export default function Support() {
  const { getShopProducts, shopCheckout, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => { getShopProducts().then((l) => setItems(l || [])).catch(() => setItems([])).finally(() => setLoading(false)); /* eslint-disable-next-line */ }, []);

  const [custom, setCustom] = useState({});

  const pay = async (p) => {
    if (!user && p.track_stock !== false) { setErr('Please sign in (or register) to buy tickets or merchandise. Donations and dues can be paid without signing in.'); return; }
    const item = { product_id: p.id, qty: 1 };
    if (p.custom_amount) {
      const dollars = parseInt(custom[p.id], 10);
      if (!dollars || dollars * 100 < (p.min_amount || 500)) { setErr(`Please enter a whole-dollar amount of at least $${(p.min_amount || 500) / 100}.`); return; }
      item.amount = dollars * 100;
    }
    setBusy(p.id); setErr('');
    try {
      const res = await shopCheckout({ items: [item], fulfillment: 'pickup' });
      window.location.href = res.checkout_url;
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.detail) || 'Could not start checkout. Please try again.');
      setBusy(null);
    }
  };

  const available = (p) => p.track_stock === false || p.stock > 0;
  const grouped = CAT_ORDER.map((cat) => ({ cat, list: items.filter((i) => (i.category || 'Merchandise') === cat) })).filter((g) => g.list.length);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 fade-in" data-testid="support-page">
      <h1 className="font-title text-3xl md:text-4xl mb-2" style={{ color: 'var(--maroon)' }}>Membership &amp; Support</h1>
      <div className="h-1 w-20 mb-4" style={{ background: 'var(--gold)' }} />
      <p className="text-[15px] mb-2">Renew your membership, make a donation, buy reunion tickets or merchandise. Payments are processed securely by Stripe. Want several things at once? <Link to="/shop" className="underline" style={{ color: 'var(--navy)' }} data-testid="support-shop-link">Use the Shop cart</Link>.</p>
      {!user && <p className="text-sm mb-6" style={{ color: 'var(--olive)' }}>Tip: <a href="/login" style={{ color: 'var(--navy)' }}>log in</a> first so your payments are recorded to your member account and member pricing applies.</p>}
      <PaymentError err={err} testid="support-error" />
      {loading && <p className="text-sm inline-flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 size={15} className="animate-spin" /> Loading…</p>}

      {grouped.map(({ cat, list }) => {
        const Icon = CAT_ICON[cat] || ShoppingBag;
        return (
          <div key={cat} className="mb-8" data-testid={`support-group-${cat}`}>
            <h2 className="flex items-center gap-2 font-title text-xl mb-3" style={{ color: 'var(--navy)' }}>
              <Icon size={20} /> {cat}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((it) => (
                <div key={it.id} className="bg-white rounded-lg p-5 shadow-sm border flex flex-col" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`support-item-${it.id}`}>
                  {it.image_url && <img src={it.image_url} alt={it.name} className="w-full h-32 object-cover rounded mb-3" />}
                  <h3 className="font-title text-lg" style={{ color: 'var(--ink)' }}>{it.name}</h3>
                  <p className="text-sm mt-1 mb-3 flex-1" style={{ color: 'var(--olive)' }}>{it.description}</p>
                  <div className="flex items-center justify-between gap-2">
                    {it.custom_amount ? (
                      <label className="flex items-center gap-1 font-title text-xl" style={{ color: 'var(--maroon)' }}>$
                        <input type="number" min={(it.min_amount || 500) / 100} step="1" inputMode="numeric" placeholder="25" value={custom[it.id] || ''}
                          onChange={(e) => setCustom({ ...custom, [it.id]: e.target.value.replace(/\D/g, '') })}
                          data-testid={`custom-amount-${it.id}`} className="w-24 px-2 py-1 rounded border text-lg" style={{ borderColor: 'rgba(0,0,0,0.2)', color: 'var(--ink)' }} />
                      </label>
                    ) : (
                    <span className="font-title text-xl" style={{ color: 'var(--maroon)' }}>
                      {money(it.your_price)}
                      {it.member_eligible && it.price_member < it.price_public && <span className="text-xs line-through ml-1" style={{ color: 'var(--olive)' }}>{money(it.price_public)}</span>}
                    </span>
                    )}
                    {available(it) ? (
                      <button
                        data-testid={`checkout-${it.id}`}
                        onClick={() => pay(it)}
                        disabled={busy === it.id}
                        className="flex items-center gap-1.5 px-4 py-2 rounded font-title text-sm transition-colors hover:opacity-90"
                        style={{ background: 'var(--navy)', color: 'var(--cream)' }}
                      >
                        {busy === it.id ? <Loader2 size={15} className="animate-spin" /> : null}
                        {CTA[cat] || 'Buy'}
                      </button>
                    ) : <span className="text-xs font-title" style={{ color: '#8a1f11' }}>Sold out</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>Sandbox mode — use test card 4242 4242 4242 4242, any future expiry and any CVC.</p>
    </div>
  );
}
